import React from 'react';
import classNames from 'classnames';
import Input, { ControlledInput } from 'src/components/ui/Input/Input';
import { useTranslation } from 'react-i18next';
import { Control, FieldErrors, useWatch } from 'react-hook-form';
import { formatAmount } from 'src/utils/general';
import { useProfile } from 'src/utils/ProfileService';
import styles from './Dimensions.module.scss';
import { OverlaySectionHeadline } from '../../OverlaySectionHeadline/OverlaySectionHeadline';
import { ProductFormValues } from '../ProductFormTypes';

export interface DimensionsProps {
  editModeActive: boolean;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  volume: number | null;
}

// The backend stores volume as m³ with digits=(5, 4). At the formatter's
// default of two digits a 30x20x10 cm product (0.006 m³) reads as 0.01, and
// anything smaller as 0.
const VOLUME_FRACTION_DIGITS = 4;

const Dimensions: React.FC<DimensionsProps> = ({ control, errors, editModeActive = false, volume }) => {
  const { t } = useTranslation();
  const { profile, loading: profileLoading } = useProfile();
  const lang = profileLoading ? null : profile.lang;
  const formatter = (v: any) => formatAmount(Number(v), lang);

  const productState = useWatch({ control, name: 'productStatus' });
  const locked = !!productState && productState !== 'Draft';
  const requiredFields = ['length', 'width', 'height', 'weight'];

  const renderInput = (name: string, label: string, key: string, unit: string) => (
    <ControlledInput
      control={control}
      name={name}
      error={!!errors[name as keyof ProductFormValues]}
      errorMessage={errors[name as keyof ProductFormValues]?.message}
      isInfoDisplay
      disabled={locked || !editModeActive}
      className="flex-1"
      type="number"
      placeholder=""
      label={`${label} ${unit} ${requiredFields.includes(key) ? '*' : ''}`}
      maxLength={20}
      formatDisplay={formatter}
      numericLocale={lang}
    />
  );

  return (
    <div className={classNames(styles.root)}>
      <OverlaySectionHeadline as="h4">{t('general.product_dimensions')}</OverlaySectionHeadline>
      <div className="flex items-center gap-16 mb-16">
        {renderInput('length', t('general.length'), 'length', t('general.in_cm'))}
        {renderInput('width', t('general.width'), 'width', t('general.in_cm'))}
        {renderInput('height', t('general.height'), 'height', t('general.in_cm'))}
      </div>
      <div className="flex items-center gap-16 mb-40">
        {renderInput('weight', t('general.weight'), 'weight', t('general.in_kg'))}
        {/* renderInput(t('general.netto_weight'), tmpProduct?.netto_weight, 'netto_weight', t('general.in_kg')) */}
        {volume !== null && (
          <Input
            isInfoDisplay
            disabled
            className="flex-1"
            type="text"
            placeholder=""
            label={t('general.volume')}
            value={volume !== null ? formatAmount(volume, lang, VOLUME_FRACTION_DIGITS) : volume}
          />
        )}
      </div>
    </div>
  );
};

export default Dimensions;
